<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar vehículo" : "Nuevo vehículo" }}
      </h1>
    </div>

    <!-- Tab Documentos (DEF-ADM-019). Solo al editar: necesita el id. -->
    <div class="tabs-bar" v-if="editing">
      <button type="button" class="tab-item" :class="{ active: tab === 'data' }" @click="tab = 'data'">
        Datos
      </button>
      <button type="button" class="tab-item" :class="{ active: tab === 'documents' }" @click="tab = 'documents'">
        Documentos
      </button>
    </div>

    <DocumentsPanel v-if="editing && tab === 'documents'" entityType="VEHICLE" :entityId="vehicleId" />

    <div v-show="tab === 'data'">
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card
          tag="article"
          no-body
          style="max-width: 760px"
          class="custom-card"
        >
          <b-card-header
            header-tag="section"
            class="custom-card-header d-flex align-items-center"
          >
            <h3>
              {{ editing ? "Datos del usuario" : "Datos del nuevo usuario" }}
            </h3>
          </b-card-header>
          <b-card-body class="custom-card-body p-0">
            <section class="form-section">
              <div>
                <h2 class="form-subtitle">Información general</h2>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="license_plate">Placa *</label>
                    <input
                      id="license_plate"
                      type="text"
                      v-model="form.license_plate"
                      autocomplete="off"
                      placeholder="Ingresar placa (ej: ABC-123)"
                      maxlength="8"
                      :disabled="editing"
                      class="custom-form-control"
                      :class="{
                        'custom-form-control-error':
                          $v.form.license_plate.$error,
                      }"
                      @keypress="(e) => { if (!/[A-Za-z0-9-]/.test(e.key)) e.preventDefault() }"
                    />
                    <FormError
                      v-if="
                        $v.form.license_plate.$error &&
                        !$v.form.license_plate.required
                      "
                      message="Placa es requerida"
                    />
                    <FormError
                      v-if="$v.form.license_plate.$error && !$v.form.license_plate.validFormat"
                      message="Formato de placa inválido (ej: ABC-123)"
                    />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label for="brand">Marca *</label>

                    <FormSelect
                      id="brand"
                      :items="brandList"
                      v-model="form.brand"
                      defaultOption="Elegir marca"
                      :showError="$v.form.brand.$error && !$v.form.brand.required"
                    />

                    <FormError
                      v-if="$v.form.brand.$error && !$v.form.brand.required"
                      message="Marca es requerida"
                    />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="model">Modelo *</label>
                    <input
                      id="model"
                      type="text"
                      v-model="form.model"
                      autocomplete="off"
                      placeholder="Ingresar modelo"
                      class="custom-form-control"
                      :class="{
                        'custom-form-control-error': $v.form.model.$error,
                      }"
                    />
                    <FormError
                      v-if="$v.form.model.$error && !$v.form.model.required"
                      message="Modelo es requerido"
                    />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label for="profile">Tipo de vehículo *</label>
                    <FormSelect
                      id="vehicle_type_id"
                      :items="vehicleTypesList"
                      v-model="form.vehicle_type_id"
                      defaultOption="Elegir tipo de vehículo"
                      :showError="$v.form.vehicle_type_id.$error"
                    />

                    <FormError
                      v-if="
                        $v.form.vehicle_type_id.$error &&
                        !$v.form.vehicle_type_id.required
                      "
                      message="Vehículo es requerido"
                    />
                  </div>
                </div>

                <!-- <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label for="capacity">Capacidad *</label>
                    <input
                      id="capacity"
                      type="number"
                      v-model="form.capacity"
                      autocomplete="off"
                      placeholder="Ingresar capacidad"
                      class="custom-form-control"
                      :class="{
                        'custom-form-control-error': $v.form.capacity.$error,
                      }"
                    />
                    <FormError
                      v-if="
                        $v.form.capacity.$error && !$v.form.capacity.required
                      "
                      message="Capacidad es requerida"
                    />
                    <FormError
                      v-if="
                        $v.form.capacity.$error && !$v.form.capacity.numeric
                      "
                      message="Capacidad debe ser un número"
                    />
                  </div>
                </div> -->

                <div class="col-md-3">
                  <div class="form-group mb-0">
                    <label for="color">Color</label>
                    <input
                      id="color"
                      type="text"
                      v-model="form.color"
                      autocomplete="off"
                      placeholder="Ej: Blanco"
                      maxlength="30"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <div class="col-md-3">
                  <div class="form-group mb-0">
                    <label for="year">Año</label>
                    <input
                      id="year"
                      type="number"
                      min="1950"
                      max="2100"
                      v-model.number="form.year"
                      autocomplete="off"
                      placeholder="Ej: 2020"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <!-- Vehículo de proveedor (DEF-ADM-018): no entra a flota ni a alquileres. -->
                <div class="col-md-6" v-if="form.provider_id">
                  <div class="form-group mb-0">
                    <label>Proveedor</label>
                    <input type="text" class="custom-form-control" :value="providerLabel" disabled />
                    <small class="text-muted">
                      Es un vehículo del proveedor: no aparece en flota ni en alquileres.
                    </small>
                  </div>
                </div>

                <div class="col-md-6" v-if="!form.provider_id">
                  <div class="form-group mb-0">
                    <label>Propiedad *</label>
                    <div class="ownership-radios">
                      <label class="radio-option">
                        <input type="radio" value="OWNED" v-model="form.ownership_type" />
                        <span>Propio</span>
                      </label>
                      <label class="radio-option">
                        <input type="radio" value="RENTED" v-model="form.ownership_type" />
                        <span>Alquilado</span>
                      </label>
                    </div>
                    <FormError
                      v-if="$v.form.ownership_type.$error && !$v.form.ownership_type.required"
                      message="Propiedad es requerida"
                    />
                  </div>
                </div>
              </div>

              <!-- Sección Alquiler (solo cuando es RENTED) — DEF-ADM-015 F4 -->
              <div v-if="form.ownership_type === 'RENTED' && !form.provider_id" class="rental-section">
                <h2 class="form-subtitle">Propiedad / Alquiler</h2>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Propietario</label>
                      <input type="text" v-model="form.owner_name" class="custom-form-control"
                        placeholder="Nombre del propietario" />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label>Teléfono</label>
                      <input type="text" v-model="form.owner_phone" class="custom-form-control"
                        placeholder="Teléfono" />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label>Email</label>
                      <input type="email" v-model="form.owner_email" class="custom-form-control"
                        placeholder="correo@dominio.com" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <div class="form-group">
                      <label>Monto alquiler</label>
                      <input type="number" step="0.01" min="0" v-model.number="form.rent_amount"
                        class="custom-form-control" placeholder="0.00" />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="form-group">
                      <label>Moneda</label>
                      <select v-model="form.rent_currency" class="custom-form-control">
                        <option value="PEN">PEN</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label>Día de pago (1–31)</label>
                      <input type="number" min="1" max="31" step="1" v-model.number="form.rent_payment_day"
                        class="custom-form-control" placeholder="Ej: 5" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <div class="form-group">
                      <label>Inicio de contrato</label>
                      <input type="date" v-model="form.contract_start_date" class="custom-form-control" />
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="form-group">
                      <label>Fin de contrato</label>
                      <input type="date" v-model="form.contract_end_date" class="custom-form-control" />
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Contrato (PDF/imagen, máx 10 MB)</label>
                      <input type="file" accept="application/pdf,image/*" class="custom-form-control"
                        @change="onContractFile($event)" />
                      <small v-if="hasContract" class="d-block mt-1">
                        <button type="button" class="link-button" @click="viewContract">Ver contrato actual</button>
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link
                      :to="{ name: 'config-vehicle-list' }"
                      class="button button-light"
                    >
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">
                      Guardar
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError, Constants } from "wize-admin";

import { VehicleService, VehicleTypeService, FleetRentalService, ProviderService } from "@/core/services";
import { DocumentsPanel } from "@/core/components";
import { Constants as LocalConstants } from "@/core/utils";
import { required } from "vuelidate/lib/validators";

export default {
  components: {
    FormError,
    FormSelect,
    DocumentsPanel,
  },
  data() {
    return {
      profileTypes: [
        LocalConstants.ProfileTypes.ADMIN,
        LocalConstants.ProfileTypes.SUPERVISOR,
      ],
      identifierTypes: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.RUC,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
      ],
      brandList: LocalConstants.VehicleBrands,
      vehicleTypesList: [],
      propertyList: [
        LocalConstants.VehicleProperty.TRAVELCAB,
        LocalConstants.VehicleProperty.EXTERNAL,
      ],
      form: {
        model: null,
        license_plate: null,
        brand: null,
        vehicle_type_id: null,
        // capacity: null,
        color: null,
        year: null,
        provider_id: null,
        ownership_type: null,
        owner_name: null,
        owner_phone: null,
        owner_email: null,
        rent_amount: null,
        rent_currency: 'PEN',
        rent_payment_day: null,
        contract_start_date: null,
        contract_end_date: null
      },
      contractFile: null,
      hasContract: false,
      provider: null,
      editing: false,
      tab: "data", // "data" | "documents" (DEF-ADM-019)
    };
  },
  validations: {
    form: {
      model: { required },
      license_plate: {
        required,
        validFormat(value) {
          if (!value) return true
          return /^[A-Z0-9]{3}-\d{3}$/.test(value)
        }
      },
      brand: { required },
      vehicle_type_id: { required },
      // capacity: { required, numeric },
      // El vehículo de un proveedor siempre es OWNED: lo fuerza el backend.
      ownership_type: {
        required(value) {
          return this.form.provider_id ? true : !!value;
        }
      }
    },
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "form.license_plate"(val) {
      if (val && !this.editing) this.form.license_plate = val.toUpperCase()
    },
  },
  computed: {
    // DEF-ADM-019 — id de la entidad para el panel de documentos.
    vehicleId() {
      return this.$route.params.id;
    },
    // DEF-ADM-018
    providerLabel() {
      if (!this.provider) return "-";
      return this.provider.business_name || this.provider.contact_name;
    },
  },
  methods: {
    async loadProvider() {
      if (!this.form.provider_id) {
        this.provider = null;
        return;
      }
      try {
        const res = await ProviderService.get(this.form.provider_id);
        this.provider = res.payload;
      } catch (e) {
        console.error(e);
        this.provider = null;
      }
    },
    async getVehicleTypes() {
      const vehicleTypes = (await VehicleTypeService.list({ status: 1 })).payload.items;
      vehicleTypes.forEach((vt) => this.vehicleTypesList.push({ key: vt.id, label: vt.name }));
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });

        return;
      }

      try {
        this.$store.dispatch("app/loading", true);

        const data = this.buildPayload();

        if (!this.editing) {
          const res = await VehicleService.save(data);
          const newId = res.payload ? res.payload.id : null;
          if (newId && this.contractFile) {
            await FleetRentalService.uploadContract(newId, this.contractFile);
          }
          Alert.success("Se almacenó la información correctamente");
          this.$router.push({ name: "config-vehicle-list" });
          return;
        } else {
          await VehicleService.update(this.$route.params.id, data);
          if (this.contractFile) {
            await FleetRentalService.uploadContract(this.$route.params.id, this.contractFile);
            this.contractFile = null;
          }
          Alert.success("Se almacenó la información correctamente");
          this.load(this.$route.params.id);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo guardar el vehículo");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    buildPayload() {
      const f = this.form;
      const base = {
        vehicle_type_id: f.vehicle_type_id,
        license_plate: f.license_plate,
        brand: String(f.brand),
        model: f.model,
        color: f.color || null,
        year: f.year != null && f.year !== "" ? Number(f.year) : null,
        provider_id: f.provider_id ? Number(f.provider_id) : null,
        ownership_type: f.provider_id ? "OWNED" : String(f.ownership_type),
      };
      // Un vehículo de proveedor nunca lleva datos de alquiler.
      if (f.provider_id || f.ownership_type !== "RENTED") return base;
      return {
        ...base,
        owner_name: f.owner_name || null,
        owner_phone: f.owner_phone || null,
        owner_email: f.owner_email || null,
        rent_amount: f.rent_amount != null && f.rent_amount !== "" ? Number(f.rent_amount) : null,
        rent_currency: f.rent_currency || "PEN",
        rent_payment_day: f.rent_payment_day != null && f.rent_payment_day !== "" ? Number(f.rent_payment_day) : null,
        contract_start_date: f.contract_start_date || null,
        contract_end_date: f.contract_end_date || null,
      };
    },
    onContractFile(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) { this.contractFile = null; return; }
      const okType = file.type === "application/pdf" || file.type.startsWith("image/");
      if (!okType) {
        Alert.error("El contrato debe ser PDF o imagen");
        event.target.value = "";
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Alert.error("El archivo supera el máximo de 10 MB");
        event.target.value = "";
        return;
      }
      this.contractFile = file;
    },
    async viewContract() {
      try {
        const res = await FleetRentalService.getContract(this.$route.params.id);
        const url = res.payload ? res.payload.url : null;
        if (url) window.open(url, "_blank");
        else Alert.info("No hay contrato disponible.");
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "No se pudo abrir el contrato.");
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await VehicleService.get(id);
        const payload = response.payload;

        this.form = {
          ...this.form,
          ...payload,
          rent_currency: payload.rent_currency || 'PEN',
        };
        this.hasContract = !!payload.has_contract;
        this.contractFile = null;
        await this.loadProvider();

        // Si el tipo de vehículo asignado no está en la lista (ej: está inactivo),
        // se agrega para que FormSelect pueda mostrar el valor actual sin limpiarlo.
        const inList = this.vehicleTypesList.find(vt => vt.key == payload.vehicle_type_id);
        if (!inList) {
          const vtResponse = await VehicleTypeService.get(payload.vehicle_type_id);
          if (vtResponse && vtResponse.payload) {
            this.vehicleTypesList.push({ key: vtResponse.payload.id, label: vtResponse.payload.name });
          }
        }

        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors.message);
        this.$router.push({ name: "form-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },
  async created() {
    await this.getVehicleTypes()
    if (this.$route.params.id) {
      this.load(this.$route.params.id);
      return;
    }
    // Alta desde el detalle de un proveedor (DEF-ADM-018).
    if (this.$route.query.provider_id) {
      this.form.provider_id = Number(this.$route.query.provider_id);
      this.form.ownership_type = "OWNED";
      await this.loadProvider();
    }
  },
};
</script>

<style lang="stylus" scoped>
.tabs-bar
  display flex
  gap 4px
  border-bottom 1px solid #e5e7eb
  margin-bottom 16px
.tab-item
  background none
  border none
  border-bottom 2px solid transparent
  padding 8px 14px
  font-weight 500
  color #6c757d
  cursor pointer
  &.active
    color #007bff
    border-bottom-color #007bff
.ownership-radios
  display flex
  gap 24px
  align-items center
  height 42px
.radio-option
  display flex
  align-items center
  gap 6px
  margin 0
  cursor pointer
  font-weight 500
  input
    cursor pointer
.rental-section
  border-top 1px solid #e5e7eb
  margin-top 8px
  padding-top 12px
.link-button
  background none
  border none
  padding 0
  color #007bff
  cursor pointer
  text-decoration underline
</style>
